import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Target, ArrowRight, AlertCircle, CheckCircle, Search, Calendar, BarChart3, Upload, FileText, X } from 'lucide-react';
import { toast } from 'sonner';

export default function RequestAccess() {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    role: '',
    organization: '',
    // Coach-specific fields
    coach_member_id: '',
    coach_certification_level: '',
    coach_city_state: '',
    coach_certification_file: null
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a PDF or image file (PNG, JPG)');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      setFileName(file.name);
      
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, coach_certification_file: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = () => {
    setFileName('');
    setFormData({ ...formData, coach_certification_file: null });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.full_name || !formData.email || !formData.role) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate archer organization requirement
    if (formData.role === 'archer' && !formData.organization) {
      setError('Organization/Club is required for archers');
      return;
    }

    // Validate coach-specific fields
    if (formData.role === 'coach') {
      if (!formData.coach_member_id) {
        setError('USA Archery Coach ID/Member ID is required');
        return;
      }
      if (!formData.coach_certification_level) {
        setError('Certification level is required');
        return;
      }
      if (!formData.coach_city_state) {
        setError('City and State are required');
        return;
      }
    }

    if (!agreedToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy');
      return;
    }

    setLoading(true);

    try {
      await authAPI.requestAccess(formData);
      setSuccess(true);
      toast.success('Access request submitted!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to submit request. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8">
        <Card className="w-full max-w-md border-slate-200 shadow-lg">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Request Submitted!</h2>
            <p className="text-slate-600 mb-6">
              Thanks! We'll review your request and send you access shortly. 
              Check your email for updates.
            </p>
            <Link to="/login">
              <Button variant="outline">
                Back to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-primary/90">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-primary rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-amber-500 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
              <Target className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Archery Manager AI</h1>
              <p className="text-white/60 text-sm">archerymanagerai.com</p>
            </div>
          </div>

          {/* Tagline */}
          <h2 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
            AI-Driven Archery<br />
            <span className="text-amber-400">Performance & Event</span><br />
            Insights
          </h2>
          
          {/* Short Pitch */}
          <p className="text-lg text-white/80 max-w-md mb-10 leading-relaxed">
            Archery Manager AI centralizes tournaments, results, and performance insights into one intelligent dashboard. Search Tournaments, Plan events, and get data-driven insights powered by advanced AI.
          </p>

          {/* Feature Pills */}
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <Search className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium">Search Tournaments</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <Calendar className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium">Plan Events</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <BarChart3 className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium">AI Insights</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Request Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Archery Manager AI</h1>
              <p className="text-sm text-slate-500">AI-Driven Performance Insights</p>
            </div>
          </div>

          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl">Request Access</CardTitle>
              <CardDescription>
                Fill out the form below and we'll get you set up
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm animate-fade-in">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    className="input-base"
                    data-testid="request-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="input-base"
                    data-testid="request-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">I am a... *</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger className="input-base" data-testid="request-role-select">
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="parent">Parent</SelectItem>
                      <SelectItem value="archer">Archer</SelectItem>
                      <SelectItem value="coach">Coach</SelectItem>
                      <SelectItem value="club">Club / Organization</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Archer-specific guidance message */}
                {formData.role === 'archer' && (
                  <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm animate-fade-in">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      Please enter your official club name exactly as registered. This ensures your coach can view your profile and enables accurate AI performance analytics.
                    </span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="organization">Organization / Club {formData.role === 'archer' || formData.role === 'coach' ? '*' : '(optional)'}</Label>
                  <Input
                    id="organization"
                    name="organization"
                    type="text"
                    placeholder="e.g., Dallas Archery Club"
                    value={formData.organization}
                    onChange={handleChange}
                    className="input-base"
                    data-testid="request-org-input"
                    required={formData.role === 'archer' || formData.role === 'coach'}
                  />
                </div>

                {/* Coach-specific fields */}
                {formData.role === 'coach' && (
                  <div className="space-y-4 p-4 bg-amber-50 border border-amber-200 rounded-lg animate-fade-in">
                    <div className="flex items-start gap-2 text-amber-700 text-sm mb-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Coach Verification Required:</strong> Please provide your credentials below. An admin will manually verify and approve your request.
                      </span>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="coach_member_id">USA Archery Coach ID / Member ID *</Label>
                      <Input
                        id="coach_member_id"
                        name="coach_member_id"
                        type="text"
                        placeholder="e.g., 123456"
                        value={formData.coach_member_id}
                        onChange={handleChange}
                        className="input-base bg-white"
                        data-testid="request-coach-id-input"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="coach_certification_level">Certification Level *</Label>
                      <Select
                        value={formData.coach_certification_level}
                        onValueChange={(value) => setFormData({ ...formData, coach_certification_level: value })}
                      >
                        <SelectTrigger className="input-base bg-white" data-testid="request-cert-level-select">
                          <SelectValue placeholder="Select certification level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Level 1 Instructor">Level 1 Instructor</SelectItem>
                          <SelectItem value="Level 2 Instructor">Level 2 Instructor</SelectItem>
                          <SelectItem value="Level 3 NTS Coach">Level 3 NTS Coach</SelectItem>
                          <SelectItem value="Level 4 NTS Coach">Level 4 NTS Coach</SelectItem>
                          <SelectItem value="Level 5 NTS Coach">Level 5 NTS Coach</SelectItem>
                          <SelectItem value="Para Archery Coach">Para Archery Coach</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="coach_city_state">City, State *</Label>
                      <Input
                        id="coach_city_state"
                        name="coach_city_state"
                        type="text"
                        placeholder="e.g., Dallas, TX"
                        value={formData.coach_city_state}
                        onChange={handleChange}
                        className="input-base bg-white"
                        data-testid="request-city-state-input"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="coach_certification_file">Certification Proof (optional)</Label>
                      <p className="text-xs text-amber-600 mb-2">Upload PDF or image (PNG, JPG) - Max 5MB</p>
                      
                      {!fileName ? (
                        <div 
                          className="border-2 border-dashed border-amber-300 rounded-lg p-4 text-center cursor-pointer hover:bg-amber-100/50 transition-colors"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <Upload className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                          <p className="text-sm text-amber-700">Click to upload certification document</p>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg"
                            onChange={handleFileChange}
                            className="hidden"
                            data-testid="request-cert-file-input"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center justify-between p-3 bg-white border border-amber-300 rounded-lg">
                          <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-amber-600" />
                            <span className="text-sm text-slate-700 truncate max-w-[200px]">{fileName}</span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={removeFile}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Terms of Service Checkbox */}
                <div className="flex items-start space-x-3 pt-2">
                  <Checkbox
                    id="terms"
                    checked={agreedToTerms}
                    onCheckedChange={(checked) => setAgreedToTerms(checked)}
                    data-testid="request-terms-checkbox"
                  />
                  <div className="grid gap-1.5 leading-none">
                    <label
                      htmlFor="terms"
                      className="text-sm text-slate-600 leading-relaxed cursor-pointer"
                    >
                      I agree to the{' '}
                      <Link
                        to="/terms"
                        target="_blank"
                        className="text-primary font-medium hover:underline"
                      >
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link
                        to="/privacy"
                        target="_blank"
                        className="text-primary font-medium hover:underline"
                      >
                        Privacy Policy
                      </Link>
                    </label>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="request-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Request Access
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-slate-600">
                  Already have an account?{' '}
                  <Link
                    to="/login"
                    className="text-primary font-medium hover:underline"
                    data-testid="login-link"
                  >
                    Sign in here
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
